/**
 * Application Entry Point
 * 
 * Main application entry file that initializes React, renders the root App
 * component, and sets up internationalization configuration. Wraps the
 * application in React.StrictMode for development warnings.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import '@/app/config/i18n/config/i18n';
import { restoreTokensOnLoad, verifyTokenIntegrity } from '@/shared/utils/token-restoration.js';
import { initViewportFix } from '@/shared/utils/viewport-fix.js';
import '@/shared/utils/versionChecker.js'; // Auto-detect new deployments
import { initErrorTracking } from '@/shared/utils/errorTracking.js';
import ErrorBoundary from '@/shared/components/ErrorBoundary.jsx';
import logger from '@/shared/utils/logger.js';


// Initialize error tracking (Sentry) if configured
initErrorTracking();

// Initialize mobile viewport height fix
initViewportFix();

// Verify and restore tokens on app initialization
try {
  const integrity = verifyTokenIntegrity();
  if (!integrity.valid && integrity.shouldClear) {
    // Clear the invalid isAuthenticated flag if no tokens exist
    localStorage.removeItem('isAuthenticated');
    logger.warn('Cleared invalid isAuthenticated flag - no tokens found');
  }

  restoreTokensOnLoad();
} catch (error) {
  logger.error('Error during token restoration on load', { error });
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
